import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import {
  Send, Mail, MessageSquare, Users, Loader2, Check, ArrowRight,
  BookOpen, Heart
} from "lucide-react";

import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { WaitlistConfirmModal } from "../components/WaitlistConfirmModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

const contactEmail = { href: "mailto:jintile@levoarc.com", label: "Email", handle: "jintile@levoarc.com" };

export default function ContactPage() {
  const [waitlistForm, setWaitlistForm] = useState({
    name: "", email: "", transition: "", zip_code: ""
  });
  const [contactForm, setContactForm] = useState({
    name: "", email: "", subject: "", message: "", inquiry_type: "general"
  });
  const [isSubmittingWaitlist, setIsSubmittingWaitlist] = useState(false);
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);
  const [showWaitlistConfirm, setShowWaitlistConfirm] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    if (!waitlistForm.name || !waitlistForm.email || !waitlistForm.zip_code || waitlistForm.zip_code.length !== 5) {
      toast.error(!waitlistForm.zip_code || waitlistForm.zip_code.length !== 5
        ? "Please enter a valid 5-digit ZIP code"
        : "Please fill in your name and email");
      return;
    }
    setIsSubmittingWaitlist(true);
    try {
      await axios.post(`${API}/waitlist`, waitlistForm);
      setWaitlistForm({ name: "", email: "", transition: "", zip_code: "" });
      setShowWaitlistConfirm(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setIsSubmittingWaitlist(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    setIsSubmittingContact(true);
    try {
      await axios.post(`${API}/contact`, contactForm);
      setContactSuccess(true);
      toast.success("Message sent! We'll get back to you soon.");
      setContactForm({ name: "", email: "", subject: "", message: "", inquiry_type: "general" });
      setTimeout(() => setContactSuccess(false), 3000);
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmittingContact(false);
    }
  };

  return (
    <div className="overflow-hidden">
      {/* Hero */}
      <section className="relative min-h-[50vh] flex items-center justify-center hero-dark" data-testid="contact-hero">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-20 w-80 h-80 bg-brand-peach/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-72 h-72 bg-brand-lavender/10 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-16 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-5 py-2 mb-8"
          >
            <Heart className="w-4 h-4 text-brand-peach" />
            <span className="text-brand-peach/90 text-sm font-medium tracking-wide">We're glad you're here</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-normal text-text-light leading-tight mb-6"
            data-testid="contact-title"
          >
            Join the <span className="gradient-text">Community</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-text-light/70 text-lg sm:text-xl max-w-2xl mx-auto"
          >
            However you want to connect, we're here.
          </motion.p>
        </div>
      </section>

      {/* Waitlist + Contact Forms */}
      <section className="py-24 bg-warm-bone" data-testid="forms-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">

            {/* Waitlist Form */}
            <motion.div {...fadeUp}>
              <div className="bg-white rounded-3xl p-8 md:p-10 shadow-lg border border-gray-100 h-full">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand-peach to-brand-lavender flex items-center justify-center">
                    <Users className="w-6 h-6 text-deep-violet" />
                  </div>
                  <div>
                    <h2 className="font-heading text-2xl text-text-dark">Join the Waitlist</h2>
                    <p className="text-text-dark/50 text-sm">Be the first to know when we launch</p>
                  </div>
                </div>
                <form onSubmit={handleWaitlistSubmit} className="space-y-5" data-testid="waitlist-page-form">
                  <div className="space-y-2">
                    <Label htmlFor="waitlist-name" className="text-text-dark">Name *</Label>
                    <Input id="waitlist-name" type="text" placeholder="Your name" value={waitlistForm.name}
                      onChange={(e) => setWaitlistForm({ ...waitlistForm, name: e.target.value })}
                      className="h-12 rounded-xl bg-gray-50 border-gray-200" data-testid="waitlist-page-name" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="waitlist-email" className="text-text-dark">Email *</Label>
                    <Input id="waitlist-email" type="email" placeholder="your@email.com" value={waitlistForm.email}
                      onChange={(e) => setWaitlistForm({ ...waitlistForm, email: e.target.value })}
                      className="h-12 rounded-xl bg-gray-50 border-gray-200" data-testid="waitlist-page-email" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="waitlist-zip" className="text-text-dark">ZIP Code *</Label>
                    <Input id="waitlist-zip" type="text" inputMode="numeric" pattern="[0-9]*" maxLength={5}
                      placeholder="ZIP Code" value={waitlistForm.zip_code}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                        setWaitlistForm({ ...waitlistForm, zip_code: val });
                      }}
                      className="h-12 rounded-xl bg-gray-50 border-gray-200 w-40" data-testid="waitlist-page-zip" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="waitlist-transition" className="text-text-dark">
                      What transformation are you navigating? <span className="text-text-dark/40">(optional)</span>
                    </Label>
                    <Textarea id="waitlist-transition" placeholder="Career change, returning to work, starting fresh..."
                      value={waitlistForm.transition}
                      onChange={(e) => setWaitlistForm({ ...waitlistForm, transition: e.target.value })}
                      className="min-h-[100px] rounded-xl bg-gray-50 border-gray-200 resize-none" data-testid="waitlist-page-transition" />
                  </div>
                  <Button type="submit" disabled={isSubmittingWaitlist}
                    className="w-full h-12 rounded-xl font-semibold transition-all bg-gradient-to-r from-brand-peach to-brand-lavender hover:shadow-[0_0_20px_rgba(245,168,130,0.4)] text-deep-violet"
                    data-testid="waitlist-page-submit">
                    {isSubmittingWaitlist ? <Loader2 className="w-5 h-5 animate-spin" /> : <><span>Join Waitlist</span><ArrowRight className="w-5 h-5 ml-2" /></>}
                  </Button>
                  <p className="text-center text-xs leading-relaxed text-gray-400" data-testid="waitlist-page-consent">
                    By submitting, you agree to our <a href="/privacy" className="underline hover:text-gray-600">Privacy Policy</a> and <a href="/terms" className="underline hover:text-gray-600">Terms of Service</a>. We'll only use your information to contact you about Become. We never sell your data.
                  </p>
                </form>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div {...fadeUp} transition={{ delay: 0.15 }}>
              <div className="bg-white rounded-3xl p-8 md:p-10 shadow-lg border border-gray-100 h-full">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-deep-violet to-brand-lavender flex items-center justify-center">
                    <MessageSquare className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="font-heading text-2xl text-text-dark">Get in Touch</h2>
                    <p className="text-text-dark/50 text-sm">Partnership, press, or just say hello</p>
                  </div>
                </div>
                <form onSubmit={handleContactSubmit} className="space-y-5" data-testid="contact-page-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="contact-name" className="text-text-dark">Name *</Label>
                      <Input id="contact-name" type="text" placeholder="Your name" value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        className="h-12 rounded-xl bg-gray-50 border-gray-200" data-testid="contact-page-name" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact-email" className="text-text-dark">Email *</Label>
                      <Input id="contact-email" type="email" placeholder="your@email.com" value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        className="h-12 rounded-xl bg-gray-50 border-gray-200" data-testid="contact-page-email" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-type" className="text-text-dark">Inquiry Type</Label>
                    <select id="contact-type" value={contactForm.inquiry_type}
                      onChange={(e) => setContactForm({ ...contactForm, inquiry_type: e.target.value })}
                      className="w-full h-12 rounded-xl bg-gray-50 border border-gray-200 px-4 text-text-dark"
                      data-testid="contact-page-type">
                      <option value="general">General Inquiry</option>
                      <option value="partnership">Partnership</option>
                      <option value="press">Press</option>
                      <option value="support">Support</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-subject" className="text-text-dark">Subject</Label>
                    <Input id="contact-subject" type="text" placeholder="How can we help?" value={contactForm.subject}
                      onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                      className="h-12 rounded-xl bg-gray-50 border-gray-200" data-testid="contact-page-subject" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-message" className="text-text-dark">Message *</Label>
                    <Textarea id="contact-message" placeholder="Tell us more..." value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      className="min-h-[120px] rounded-xl bg-gray-50 border-gray-200 resize-none" data-testid="contact-page-message" />
                  </div>
                  <Button type="submit" disabled={isSubmittingContact || contactSuccess}
                    className={`w-full h-12 rounded-xl font-semibold transition-all ${contactSuccess ? "bg-green-500 hover:bg-green-500" : "bg-deep-violet hover:bg-deep-violet/90"} text-white`}
                    data-testid="contact-page-submit">
                    {isSubmittingContact ? <Loader2 className="w-5 h-5 animate-spin" /> :
                      contactSuccess ? <><Check className="w-5 h-5 mr-2" />Message Sent!</> :
                      <><span>Send Message</span><Send className="w-5 h-5 ml-2" /></>}
                  </Button>
                  <p className="text-center text-xs leading-relaxed text-gray-400" data-testid="contact-page-consent">
                    By submitting, you agree to our <a href="/privacy" className="underline hover:text-gray-600">Privacy Policy</a> and <a href="/terms" className="underline hover:text-gray-600">Terms of Service</a>. We'll only use your information to contact you about Become. We never sell your data.
                  </p>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Blog + Socials Section */}
      <section className="py-24 bg-white" data-testid="community-connect-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">

            {/* Blog Card */}
            <motion.div {...fadeUp}>
              <div className="relative rounded-3xl overflow-hidden bg-deep-violet p-10 md:p-12 h-full flex flex-col justify-between min-h-[320px]"
                data-testid="community-blog-card">
                <div className="absolute top-0 right-0 w-64 h-64 bg-brand-peach/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" />
                <div className="relative z-10">
                  <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center mb-6">
                    <BookOpen className="w-7 h-7 text-brand-peach" />
                  </div>
                  <h3 className="font-heading text-2xl sm:text-3xl text-white mb-4">
                    Read the Blog
                  </h3>
                  <p className="text-white/70 text-base leading-relaxed max-w-md">
                    Real insights on transformation, career change, and navigating what comes next.
                    Written by our team, for people in the middle of something big.
                  </p>
                </div>
                <div className="relative z-10 mt-8">
                  <Link to="/blog">
                    <Button className="rounded-full h-12 px-8 bg-white text-deep-violet font-semibold hover:bg-white/90 hover:-translate-y-0.5 transition-all duration-300"
                      data-testid="community-blog-link">
                      <BookOpen className="w-5 h-5 mr-2" />
                      Explore the Blog
                    </Button>
                  </Link>
                </div>
              </div>
            </motion.div>

            {/* Contact Card */}
            <motion.div {...fadeUp} transition={{ delay: 0.15 }}>
              <div className="rounded-3xl bg-warm-bone p-10 md:p-12 h-full flex flex-col justify-between min-h-[320px] border border-gray-100"
                data-testid="community-contact-card">
                <div>
                  <h3 className="font-heading text-2xl sm:text-3xl text-text-dark mb-3">
                    Get in Touch
                  </h3>
                  <p className="text-text-dark/60 text-base mb-8 max-w-md">
                    Have a question or just want to say hello? We'd love to hear from you.
                  </p>
                </div>
                <div className="space-y-4">
                  <a href={contactEmail.href}
                    className="flex items-center gap-4 p-4 rounded-2xl bg-white border border-gray-100 hover:border-brand-peach/40 hover:shadow-md transition-all duration-300 group"
                    data-testid="community-contact-email">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-brand-peach/20 to-brand-lavender/20 flex items-center justify-center group-hover:from-brand-peach/30 group-hover:to-brand-lavender/30 transition-colors">
                      <Mail className="w-5 h-5 text-deep-violet" />
                    </div>
                    <div className="flex-1">
                      <p className="text-text-dark font-medium text-sm">{contactEmail.label}</p>
                      <p className="text-text-dark/50 text-sm">{contactEmail.handle}</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-text-dark/30 group-hover:text-brand-primary group-hover:translate-x-1 transition-all" />
                  </a>
                </div>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      <WaitlistConfirmModal isOpen={showWaitlistConfirm} onClose={() => setShowWaitlistConfirm(false)} />
    </div>
  );
}
